// The site plan's access lines on the device, and their copy on SharePoint
// (Site plan (access).jpg: the plan with the lines and no pins, for the
// proposal's Access Plan). A line is saved on its visit area like any change
// (device first, then the outbox), so drawing works offline; the copy needs
// signal and is saved when the measurer taps Save, like the marked-up copy.
import { accessFingerprint, type AccessLine } from '@higgins/condition-library';
import { currentSides, drawAccessLines, withLineAdded, withLineRemoved } from './access-lines';
import { ensureVisitArea } from './condition-records';
import { patchFields, type DeviceRecord } from './device-db';
import { planCanvas, usePlanCopy } from './marked-up-plan';
import { planVersion, toJpeg, type SitePlanDoc } from './site-plan';

/** Draw a line along an area: on the area's visit area (made now, if this is its first condition record). */
export async function addAccessLine(visit: DeviceRecord, area: DeviceRecord, line: AccessLine): Promise<string> {
  const visitArea = await ensureVisitArea(visit, area);
  await patchFields(visitArea.id, withLineAdded(line));
  return visitArea.id;
}

/** Take one line off its visit area. */
export const removeAccessLine = (visitAreaId: string, lineId: string): Promise<void> => patchFields(visitAreaId, withLineRemoved(lineId));

/** The fingerprint of the lines on the plan now, as the server works it out from CRM (access-plan.ts in api/). */
function accessLinesFingerprint(plan: SitePlanDoc, records: readonly DeviceRecord[]): string {
  const sideOf = currentSides(records);
  return accessFingerprint(
    planVersion(plan),
    records.filter((r) => r.table === 'visitarea').map((v) => ({ id: v.id, side: sideOf(v), accessLines: v.fields.hig_accesslines })),
  );
}

/** The plan with its access lines drawn on, as a JPEG the plan's own size. */
async function drawAccessPlan(image: string, plan: SitePlanDoc, lines: readonly AccessLine[]): Promise<Blob> {
  const { canvas, ctx } = await planCanvas(image, plan);
  drawAccessLines(ctx, plan, lines);
  return toJpeg(canvas);
}

/** The access copy's state for the plan and lines on screen, and Save to draw and save it (needs signal). */
export function useAccessPlan(visit: DeviceRecord | undefined, plan: SitePlanDoc | null, image: string | null, records: readonly DeviceRecord[], lines: readonly AccessLine[]) {
  const fingerprint = plan ? accessLinesFingerprint(plan, records) : null;
  return usePlanCopy(visit, plan, image, 'access', fingerprint, (img, p) => drawAccessPlan(img, p, lines));
}
